import {Injectable} from '@angular/core';
import {EnvironmentService} from '../environment.service';
import {catchError, from, lastValueFrom, map, Observable, throwError} from 'rxjs';
import {
  CalculateActiveResponse,
  ICalculateRequest,
  INewSubscriptionData,
  IPaymentMethodRequest,
  IStripeSubscriptionResponse,
  ISwitchToAnnual,
} from '../../types/payment.type';
import {HttpClient} from '@angular/common/http';
import {ApiClient} from './userway-api.service';
import {
  CreateInvoiceBodyDto,
  CalculateSwitchPlanBodyDto,
  DeletePaymentMethodBodyDto,
  OneTimeChargeBodyDto,
  PaymentFormOrigin,
  CalculateCartBodyDto,
  CalculateCartTrialPayNowBodyDto,
  CartRemediationUpgradeBodyDto,
  CreateUserSharedPaymentLinkDto,
  ConfirmInvoiceBodyDto,
  CreateSubscriptionBodyDto,
  PaymentMethodCreateBodyDto,
  PaymentMethodUpdateBodyDto,
  V1BillingStripeTCustomerListData,
  V1BillingStripeTOneTimePaymentCardWalletListData,
  UwV2BillingStripePaymentMethodsDefaultListData,
  UwV2BillingStripePaymentMethodsSubscriptionsDetail2Data,
  V1BillingStripeTOneTimePaymentInvoiceCreateData,
  V1BillingStripeTCartCalculateCreateData,
  V1BillingStripeTCartCalculateRemediationUpgradeCreateData,
  V1BillingStripeTCartTrialPayNowCreateData,
  RetryInvoiceBodyDto,
  V1BillingStripeTSubscriptionCreateData,
  V1BillingStripeTSubscriptionRetryInvoiceCreateData,
  UwV2BillingStripeHelperProductsCreateData,
  UwV2BillingStripePaymentMethodsListData,
  UpgradeRemediationBody,
  CartCalculateQuoteTotalsDto,
  UwV2BillingCartCalculateQuoteCreateData,
  LaunchQuoteSubscriptionDto,
  UwV2BillingSubscriptionsLaunchQuoteSubscriptionCreateData,
  CheckPaymentMethodAddressBodyDto,
  UwV2BillingStripePaymentMethodsCheckAddressCreateData,
  ConfirmSpecialPaymentBody,
  ConfirmSpecialPaymentResponse,
  UwV2BillingSpecialPaymentsConfirmCreateData,
  UwV2BillingSubscriptionsCreateAchSetupIntentCreateData,
} from '../../../userway/sdk/UserWayApiClient';
import {AxiosHeaders} from 'axios';

@Injectable({
  providedIn: 'root',
})
export class StripeApiService {
  constructor(
    private env: EnvironmentService,
    private http: HttpClient,
    private apiClient: ApiClient,
  ) {}

  getProducts(identityHash?: string): Observable<UwV2BillingStripeHelperProductsCreateData> {
    const request = this.apiClient.api.stripe.uwV2BillingStripeHelperProductsCreate({spltoken: identityHash}, null);
    return this.apiClient.fetch(request);
  }

  getMultiPaymentMethods(): Observable<UwV2BillingStripePaymentMethodsListData> {
    const request = this.apiClient.api.stripe.uwV2BillingStripePaymentMethodsList();
    return this.apiClient.fetch(request);
  }

  addMultiPaymentMethods(data: PaymentMethodCreateBodyDto): Observable<any> {
    const request = this.apiClient.api.stripe.uwV2BillingStripePaymentMethodsCreate(data);
    return from(request).pipe(map(data => data));
  }

  updatePaymentMethod(paymentDataId: number, data: PaymentMethodUpdateBodyDto): Observable<any> {
    const request = this.apiClient.api.stripe.uwV2BillingStripePaymentMethodsUpdate(String(paymentDataId), data);
    return from(request).pipe(map(data => data));
  }

  getSubscriptionsOfPaymentMethod(paymentId: number, searchBy?: string): Observable<any> {
    const request = this.apiClient.api.stripe.uwV2BillingStripePaymentMethodsSubscriptionsDetail({
      paymentDataId: String(paymentId),
      search: searchBy,
    });
    return this.apiClient.fetch(request);
  }

  setDefaultPaymentMethod(paymentId: number): Observable<any> {
    const request = this.apiClient.api.stripe.uwV2BillingStripePaymentMethodsIsDefaultUpdate(paymentId.toString());
    return this.apiClient.fetch(request);
  }

  getPaymentMethodBySubscriptionId(
    subscriptionId: number,
  ): Observable<UwV2BillingStripePaymentMethodsSubscriptionsDetail2Data> {
    const request = this.apiClient.api.stripe.uwV2BillingStripePaymentMethodsSubscriptionsDetail2(
      String(subscriptionId),
    );
    return this.apiClient.fetch(request);
  }

  getDefaultPaymentMethod(): Observable<UwV2BillingStripePaymentMethodsDefaultListData> {
    const request = this.apiClient.api.stripe.uwV2BillingStripePaymentMethodsDefaultList();
    return this.apiClient.fetch(request);
  }

  removePaymentMethod(paymentId: number): Observable<any> {
    const request = this.apiClient.api.stripe.uwV2BillingStripePaymentMethodsDelete(String(paymentId));
    return this.apiClient.fetch(request);
  }

  getCardWallet(identityHash?: string): Observable<V1BillingStripeTOneTimePaymentCardWalletListData> {
    const headers = this.createAxiosCustomHeaderContext(identityHash);

    const request = this.apiClient.api.billing.v1BillingStripeTOneTimePaymentCardWalletList({headers});
    return this.apiClient.fetch(request);
  }

  getOrCreateCustomer(identityHash?: string): Observable<V1BillingStripeTCustomerListData> {
    const headers = this.createAxiosCustomHeaderContext(identityHash);

    const request = this.apiClient.api.stripe.v1BillingStripeTCustomerList({headers});
    return this.apiClient.fetch(request);
  }

  createNewSubscription(
    data: CreateSubscriptionBodyDto,
    identityHash?: string,
  ): Observable<V1BillingStripeTSubscriptionCreateData> {
    const headers = this.createAxiosCustomHeaderContext(identityHash);
    const request = this.apiClient.api.subscriptions.v1BillingStripeTSubscriptionCreate(data, {headers});
    return this.apiClient.fetch(request);
  }

  createNewSubscriptionInvoice(
    data: CreateInvoiceBodyDto,
    identityHash?: string,
  ): Observable<V1BillingStripeTOneTimePaymentInvoiceCreateData> {
    const headers = new AxiosHeaders();

    if (identityHash) {
      headers.set(this.env.SHARED_PAYMENT_REQUESTS_HEADER_NAME, identityHash);
    }

    const request = this.apiClient.api.billing.v1BillingStripeTOneTimePaymentInvoiceCreate(data, {headers});

    return this.apiClient.fetch(request);
  }

  retryInvoice(
    data: RetryInvoiceBodyDto & {customerId?: number},
    identityHash?: string,
  ): Observable<V1BillingStripeTSubscriptionRetryInvoiceCreateData> {
    const headers = this.createAxiosCustomHeaderContext(identityHash);
    const request = this.apiClient.api.subscriptions.v1BillingStripeTSubscriptionRetryInvoiceCreate(data, {headers});
    return this.apiClient.fetch(request);
  }

  confirmInvoice(data: ConfirmInvoiceBodyDto, identityHash?: string): Observable<any> {
    const headers = this.createAxiosCustomHeaderContext(identityHash);
    const request = this.apiClient.api.subscriptions.v1BillingStripeTSubscriptionConfirmInvoiceCreate(data, {headers});
    return this.apiClient.fetch(request);
  }

  deletePaymentMethod(data: IPaymentMethodRequest) {
    const deleteData: DeletePaymentMethodBodyDto = {
      paymentMethodId: data.paymentMethodId,
      formOrigin: PaymentFormOrigin.MANAGE_CONSOLE,
    };
    const request = this.apiClient.api.stripe.v1BillingStripeTPaymentMethodDeleteCreate(deleteData);
    return from(request).pipe(map(data => data));
  }

  getInvoiceToken(data: OneTimeChargeBodyDto) {
    const request = this.apiClient.api.billing.v1BillingStripeOneTimePaymentChargeCreate({
      paymentIntentId: data.paymentIntentId,
      meta: data.meta,
      formOrigin: PaymentFormOrigin.MANAGE_CONSOLE,
    });
    return from(request).pipe(
      map(data => {
        return data.data;
      }),
    );
  }

  calculateSwitchPlan(data: ISwitchToAnnual) {
    const switchPlanData: CalculateSwitchPlanBodyDto = {
      customerId: data.customerId,
      subscriptionId: data.subscriptionId,
      formOrigin: PaymentFormOrigin.MANAGE_CONSOLE,
    };
    const request = this.apiClient.api.stripe.v1BillingStripeTCartCalculateSwitchPlanCreate({}, switchPlanData);
    return from(request).pipe(map(data => data.data));
  }

  calculateUpgradeRemediation(
    data: CartRemediationUpgradeBodyDto,
  ): Observable<V1BillingStripeTCartCalculateRemediationUpgradeCreateData> {
    const request = this.apiClient.api.stripe.v1BillingStripeTCartCalculateRemediationUpgradeCreate({}, data);
    return this.apiClient.fetch(request);
  }

  calculate(data: CalculateCartBodyDto, identityHash?: string): Observable<V1BillingStripeTCartCalculateCreateData> {
    const request = this.apiClient.api.stripe.v1BillingStripeTCartCalculateCreate(
      {
        spltoken: identityHash,
      },
      data,
    );
    return this.apiClient.fetch(request);
  }

  calculateRemediationUpgrade(
    data: CartRemediationUpgradeBodyDto,
    identityHash?: string,
  ): Observable<V1BillingStripeTCartCalculateRemediationUpgradeCreateData> {
    const request = this.apiClient.api.stripe.v1BillingStripeTCartCalculateRemediationUpgradeCreate(
      {
        spltoken: identityHash,
      },
      data,
    );
    return this.apiClient.fetch(request);
  }

  calculatePdfSubscription(data: ICalculateRequest): Observable<CalculateActiveResponse> {
    return this.http.post<CalculateActiveResponse>(
      `${this.env.apiUrl.apiBaseV1}billing/stripe/cart/calculate-subscription-upgrade`,
      data,
    );
  }

  updateRemediationProSubscription(data: UpgradeRemediationBody, identityHash: string) {
    const headers = this.createAxiosCustomHeaderContext(identityHash);
    const request = this.apiClient.api.subscriptions.v1BillingStripeTSubscriptionUpgradeRemediationCreate(data, {
      headers,
    });

    return this.apiClient.fetch(request);
  }

  updatePdfSubscription(
    data: Partial<INewSubscriptionData>,
    userwaySubscription: string,
  ): Promise<IStripeSubscriptionResponse> {
    return lastValueFrom(
      this.http
        .post<IStripeSubscriptionResponse>(
          `${this.env.apiUrl.apiBaseV1}billing/stripe/subscription/${userwaySubscription}/upgrade`,
          data,
        )
        .pipe(
          catchError(error => throwError(error)),
          map(response => {
            return response;
          }),
        ),
    );
  }

  calculateOnTrial(data: CalculateCartTrialPayNowBodyDto): Observable<V1BillingStripeTCartTrialPayNowCreateData> {
    const request = this.apiClient.api.stripe.v1BillingStripeTCartTrialPayNowCreate({}, data);
    return this.apiClient.fetch(request);
  }

  calculateQuote(
    data: CartCalculateQuoteTotalsDto,
    identityHash?: string,
  ): Observable<UwV2BillingCartCalculateQuoteCreateData> {
    const headers = this.createAxiosCustomHeaderContext(identityHash);
    const request = this.apiClient.api.billing.uwV2BillingCartCalculateQuoteCreate(data, {headers});
    return this.apiClient.fetch(request);
  }

  createQuoteSubscription(
    data: LaunchQuoteSubscriptionDto,
    identityHash?: string,
  ): Observable<UwV2BillingSubscriptionsLaunchQuoteSubscriptionCreateData> {
    const headers = this.createAxiosCustomHeaderContext(identityHash);
    const request = this.apiClient.api.billing.uwV2BillingSubscriptionsLaunchQuoteSubscriptionCreate(data, {headers});
    return this.apiClient.fetch(request);
  }

  confirmSpecialPayment(
    data: ConfirmSpecialPaymentBody,
    identityHash?: string,
  ): Observable<UwV2BillingSpecialPaymentsConfirmCreateData> {
    const headers = this.createAxiosCustomHeaderContext(identityHash);
    const request = this.apiClient.api.billing.uwV2BillingSpecialPaymentsConfirmCreate(data, {
      headers,
    });
    return this.apiClient.fetch(request);
  }

  createAchSetupIntent(identityHash?: string): Observable<UwV2BillingSubscriptionsCreateAchSetupIntentCreateData> {
    const headers = this.createAxiosCustomHeaderContext(identityHash);
    const request = this.apiClient.api.billing.uwV2BillingSubscriptionsCreateAchSetupIntentCreate({headers});
    return this.apiClient.fetch(request);
  }

  // TODO need to update interface when backend is ready
  generatePaymentLink(data: CreateUserSharedPaymentLinkDto): Observable<any> {
    const request = this.apiClient.api.spl.v1SplCreateUserSharedCreate(data);
    return this.apiClient.fetch(request);
  }

  checkCardAddress(
    data: CheckPaymentMethodAddressBodyDto,
  ): Observable<UwV2BillingStripePaymentMethodsCheckAddressCreateData> {
    const request = this.apiClient.api.stripe.uwV2BillingStripePaymentMethodsCheckAddressCreate(data);
    return this.apiClient.fetch(request);
  }

  private getHeaders(identityHash: string) {
    return identityHash ? {headers: {[this.env.SHARED_PAYMENT_REQUESTS_HEADER_NAME]: identityHash}} : {};
  }

  private createAxiosCustomHeaderContext(identityHash: string) {
    const axiosHeaders = new AxiosHeaders();

    if (identityHash) {
      axiosHeaders.set(this.env.SHARED_PAYMENT_REQUESTS_HEADER_NAME, identityHash);
    }

    return axiosHeaders;
  }
}
